export interface IBundlePlan {
  id: string;
  name: string;
  quota: string;
  price: number;
}

export interface IBundleApp {
  id: 'trakow' | 'paypal' | 'returns';
  name: string;
  letter: string;
  color: string;
  logo: string;
  desc: string;
  isCurrent: boolean;
  currentPlanId: string;
  storeUrl?: string;
  adminPath?: string;
  plans: IBundlePlan[];
  isInstalled?: boolean;
}

export const buildAdminUrl = (shop: string | undefined, adminPath: string | undefined): string => {
  if (!shop || !adminPath) return '';
  const shopName = shop.replace(/\.myshopify\.com$/i, '');
  return `https://admin.shopify.com/store/${shopName}/apps/${adminPath}`;
};

export const discountRate = (addOnCount: 0 | 1 | 2): number => {
  if (addOnCount === 2) return 0.3;
  if (addOnCount === 1) return 0.2;
  return 0;
};

export const discountCode = (rate: number): string => {
  if (rate >= 0.3) return 'SYNCTRACK-BUNDLE-30';
  if (rate >= 0.2) return 'SYNCTRACK-SUITE-20';
  return '';
};

export const BUNDLE_APPS: IBundleApp[] = [
  {
    id: 'trakow',
    name: 'Synctrack AI Order Tracking',
    letter: 'T',
    color: '#1a1a2e',
    logo: 'https://media.synctrack.io/megamind-ordertracking/uploads/2026/05/3a2b6eb2-8e48-48e3-9125-ccd8bc206a64.png',
    desc: "Keeps customers informed from dispatch to delivery — fewer 'where is my order?' tickets.",
    isCurrent: true,
    currentPlanId: 'basic',
    plans: [
      { id: 'starter', name: 'Free', quota: '50 shipments/mo', price: 0 },
      { id: 'basic', name: 'Launch', quota: '500 shipments/mo', price: 11.99 },
      { id: 'professional', name: 'Scale', quota: '2,000 shipments/mo', price: 29.99 },
      { id: 'enterprise', name: 'Pro', quota: 'Unlimited shipments', price: 79.99 },
    ],
  },
  {
    id: 'paypal',
    name: 'Synctrack PayPal Tracking Sync',
    letter: 'P',
    color: '#003087',
    logo: 'https://media.synctrack.io/megamind-ordertracking/uploads/2026/05/58e8d903-79f3-4559-91d7-d0d0d7c6c0c7.png',
    desc: "Closes the gap between your shipping carrier and PayPal — so fulfilled orders don't turn into chargebacks.",
    isCurrent: false,
    currentPlanId: '',
    storeUrl: 'https://apps.shopify.com/synctrack?utm_campaign=crs&utm_source=trakow&utm_medium=bundlepage',
    adminPath: 'add-tracking-paypal-auto/apps/account',
    plans: [
      { id: 'starter', name: 'Starter', quota: '50 orders/mo', price: 0 },
      { id: 'basic', name: 'Basic', quota: '500 orders/mo', price: 8.99 },
      { id: 'pro', name: 'Pro', quota: '2,000 orders/mo', price: 15.99 },
      { id: 'unlimited', name: 'Unlimited', quota: 'Unlimited orders', price: 39.99 },
    ],
  },
  {
    id: 'returns',
    name: 'Synctrack Returns & Exchanges',
    letter: 'R',
    color: '#1a7a4a',
    logo: 'https://media.synctrack.io/megamind-ordertracking/asset/logo_return.png',
    desc: 'Turns the moment a customer wants to return into a chance to retain them — automated, on your terms.',
    isCurrent: false,
    currentPlanId: '',
    storeUrl: 'https://apps.shopify.com/omega-returns-drive?utm_campaign=crs&utm_source=trakow&utm_medium=bundlepage',
    adminPath: 'returns-drive-1/plan',
    plans: [
      { id: 'basic', name: 'Basic', quota: '20 returns/mo', price: 9.99 },
      { id: 'pro', name: 'Pro', quota: '100 returns/mo', price: 19.99 },
      { id: 'premium', name: 'Premium', quota: 'Unlimited returns', price: 49.99 },
    ],
  },
];

export const DEFAULT_PLAN_SELECTIONS: Record<'trakow' | 'paypal' | 'returns', string> = {
  trakow: 'basic',
  paypal: 'basic',
  returns: 'pro',
};

export const DEFAULT_APP_STATES: Record<'paypal' | 'returns', boolean> = {
  paypal: true,
  returns: true,
};

export const ADDON_APPS = BUNDLE_APPS.slice(1) as Array<IBundleApp & { id: 'paypal' | 'returns'; storeUrl: string }>;
